<template>
  <div v-if="widget" class="pointer-events-none fixed inset-x-0 bottom-0 z-[90] mx-auto h-0 w-full max-w-screen-sm desk:max-w-[1200px]">
    <!-- 靠左，落在購物車 pl-[112px] 預留位，跟現有 SaleSmartly 泡泡同一側，避免蓋住結算列。 -->
    <button
      type="button"
      class="pointer-events-auto absolute bottom-16 left-3 flex h-14 w-14 flex-col items-center justify-center rounded-full bg-primary text-white shadow-[0_8px_18px_rgba(0,0,0,0.2)] sm:bottom-14"
      :aria-label="bubbleLabel"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
    >
      <svg viewBox="0 0 24 24" class="h-5 w-5" fill="currentColor" aria-hidden="true">
        <path d="M12 3a8 8 0 00-8 8v1.2A2.8 2.8 0 006.8 15H8a1 1 0 011 1v3.2a1.8 1.8 0 01-1.8 1.8H6a3 3 0 01-3-3V11a9 9 0 0118 0v7a3 3 0 01-3 3h-1.2a1.8 1.8 0 01-1.8-1.8V16a1 1 0 011-1h1.2A2.8 2.8 0 0020 12.2V11a8 8 0 00-8-8z" />
      </svg>
      <span class="mt-0.5 text-[10px] font-medium leading-none">客服</span>
      <span
        v-if="unread > 0"
        class="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold leading-none text-white"
      >
        {{ unread > 99 ? "99+" : unread }}
      </span>
    </button>

    <div
      v-if="frameMounted"
      :class="open ? 'flex' : 'hidden'"
      class="pointer-events-auto fixed inset-0 z-[100] flex-col bg-white sm:absolute sm:inset-auto sm:bottom-36 sm:left-3 sm:h-[600px] sm:max-h-[calc(100vh-9rem)] sm:w-[380px] sm:overflow-hidden sm:rounded-xl sm:border sm:border-gray-100 sm:shadow-[0_10px_28px_rgba(0,0,0,0.14)]"
      role="dialog"
      aria-label="線上客服"
    >
      <button
        v-if="!widgetReady"
        type="button"
        class="absolute right-3 top-3 z-20 rounded bg-white px-2 py-1 text-xs text-gray-500"
        @click="open = false"
      >
        關閉
      </button>
      <iframe
        v-if="frameMounted"
        ref="frame"
        :src="widget.href"
        title="線上客服"
        class="relative z-0 min-h-0 w-full flex-1 border-0 bg-white"
        referrerpolicy="origin"
        @load="onFrameLoad"
      />
    </div>
  </div>
</template>

<script>
import chatWidgetMode from "~/utils/chatWidgetMode";

function readWidget() {
  // 舊的 .env 沒有這個鍵時，客戶端可能讀不到 process.env.CHAT_WIDGET_URL。
  try {
    return chatWidgetMode.parseChatWidgetTarget(process.env.CHAT_WIDGET_URL);
  } catch (error) {
    return null;
  }
}

export default {
  data() {
    return {
      open: false,
      frameMounted: false,
      widgetReady: false,
      unread: 0,
    };
  },
  computed: {
    widget() {
      return readWidget();
    },
    authLoggedIn() {
      return Boolean(this.$auth && this.$auth.loggedIn);
    },
    bubbleLabel() {
      if (this.unread > 0) return `客服，${this.unread} 則未讀`;
      return "客服";
    },
  },
  watch: {
    authLoggedIn() {
      this.sendAuth();
    },
  },
  mounted() {
    if (typeof window === "undefined") return;
    window.addEventListener("message", this.onMessage);
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    if (typeof window === "undefined") return;
    window.removeEventListener("message", this.onMessage);
    window.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    toggle() {
      if (this.open) {
        this.open = false;
        return;
      }
      this.frameMounted = true;
      this.open = true;
    },
    accessToken() {
      const strategy = this.$auth && this.$auth.strategy && this.$auth.strategy.name;
      if (!strategy || typeof this.$auth.getToken !== "function") return "";
      const raw = this.$auth.getToken(strategy);
      if (typeof raw !== "string") return "";
      const prefix = "Bearer ";
      return raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
    },
    postAuth() {
      const widget = this.widget;
      if (!widget) return;
      const frame = this.$refs.frame;
      const target = frame && frame.contentWindow;
      if (!target) return;
      const payload = { type: "gm-chat:auth" };
      if (this.authLoggedIn) payload.access_token = this.accessToken();
      target.postMessage(payload, widget.origin);
    },
    sendAuth() {
      if (!this.widgetReady) return;
      this.postAuth();
    },
    onFrameLoad() {
      this.postAuth();
    },
    onKeydown(event) {
      if (event.key === "Escape" && this.open) this.open = false;
    },
    onMessage(event) {
      const widget = this.widget;
      if (!widget || event.origin !== widget.origin) return;
      const frame = this.$refs.frame;
      if (frame && event.source !== frame.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "gm-chat:ready") {
        this.widgetReady = true;
        this.$nextTick(() => this.sendAuth());
        return;
      }
      if (data.type === "gm-chat:unread") {
        const count = typeof data.count === "number" ? data.count : Number(data.count);
        this.unread = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
        return;
      }
      if (data.type === "gm-chat:close") this.open = false;
    },
  },
};
</script>
